import { Header, notify, StatsCard } from "@general-backoffice/core";
import React, { type ChangeEvent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Spinner,
  Table,
} from "reactstrap";

import { type AuthLog, authLogsEndpoints, type AuthLogStats, type StrapiId } from "../../../../api";

interface Filters {
  status: string;
  email: string;
  ipAddress: string;
  origin: string;
  action: string;
  startDate: string;
  endDate: string;
}

interface PaginationState {
  page: number;
  pageSize: number;
  total: number;
  pageCount: number;
}

const STATUS_OPTIONS = [
  { value: "", label: "All Status" },
  { value: "success", label: "Success" },
  { value: "failed", label: "Failed" },
];

const STATUS_COLORS: Record<string, string> = {
  success: "success",
  failed: "danger",
};

const ORIGIN_OPTIONS = [
  { value: "", label: "All Origins" },
  { value: "mobile", label: "Mobile" },
  { value: "website", label: "Website" },
  { value: "unknown", label: "Unknown" },
];

const ORIGIN_ICONS: Record<string, string> = {
  mobile: "fas fa-mobile-alt",
  website: "fas fa-globe",
  unknown: "fas fa-question-circle",
};

const ORIGIN_COLORS: Record<string, string> = {
  mobile: "info",
  website: "primary",
  unknown: "secondary",
};

const ACTION_OPTIONS = [
  { value: "", label: "All Actions" },
  { value: "login", label: "Login" },
  { value: "password_reset_request", label: "Password Reset Request" },
  { value: "password_reset", label: "Password Reset" },
];

const ACTION_COLORS: Record<string, string> = {
  login: "primary",
  password_reset_request: "warning",
  password_reset: "info",
};

const AuthLogs: React.FC = () => {
  const { t } = useTranslation("dashboard", { keyPrefix: "authLogs" });

  // Stats state
  const [stats, setStats] = useState<AuthLogStats>({
    total: 0,
    success: 0,
    failed: 0,
  });
  const [isLoadingStats, setIsLoadingStats] = useState(true);

  // Logs state
  const [logs, setLogs] = useState<AuthLog[]>([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters state
  const [filters, setFilters] = useState<Filters>({
    status: "",
    email: "",
    ipAddress: "",
    origin: "",
    action: "",
    startDate: "",
    endDate: "",
  });

  // Pagination state
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    pageSize: 25,
    total: 0,
    pageCount: 0,
  });

  // View modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuthLog | null>(null);

  const loadStats = useCallback(async () => {
    try {
      setIsLoadingStats(true);
      const { data } = await authLogsEndpoints.getStats({
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
      });
      setStats(data || { total: 0, success: 0, failed: 0 });
    } catch (err: unknown) {
      console.error("Failed to load stats:", err);
    } finally {
      setIsLoadingStats(false);
    }
  }, [filters.startDate, filters.endDate]);

  const loadLogs = useCallback(async () => {
    try {
      setIsLoadingLogs(true);
      setError(null);

      const { data, meta } = await authLogsEndpoints.getList({
        pagination: {
          page: pagination.page,
          pageSize: pagination.pageSize,
        },
        status: filters.status || undefined,
        email: filters.email || undefined,
        ipAddress: filters.ipAddress || undefined,
        origin: filters.origin || undefined,
        action: filters.action || undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
      });

      setLogs(data || []);
      setPagination((prev) => ({
        ...prev,
        total: meta?.pagination?.total || 0,
        pageCount: meta?.pagination?.pageCount || Math.ceil((meta?.pagination?.total || 0) / prev.pageSize),
      }));
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "Failed to load logs";
      setError(errorMessage);
    } finally {
      setIsLoadingLogs(false);
    }
  }, [filters, pagination.page, pagination.pageSize]);

  // Load data on mount and filter change
  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleFilterChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  const handleClearFilters = () => {
    setFilters({
      status: "",
      email: "",
      ipAddress: "",
      origin: "",
      action: "",
      startDate: "",
      endDate: "",
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const openViewModal = async (logId: StrapiId) => {
    try {
      const response = await authLogsEndpoints.getOne(logId);
      setSelectedLog(response.data);
      setViewModalOpen(true);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "Failed to load event details";
      notify.error(errorMessage);
    }
  };

  const closeViewModal = () => {
    setViewModalOpen(false);
    setSelectedLog(null);
  };

  const formatDate = (dateString: string | undefined | null): string => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const truncateText = (text: string | null | undefined, maxLength: number): string => {
    if (!text) return "-";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Generate pagination items
  const renderPagination = () => {
    const items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, pagination.page - Math.floor(maxVisible / 2));
    const endPage = Math.min(pagination.pageCount, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    // Previous button
    items.push(
      <PaginationItem key="prev" disabled={pagination.page === 1}>
        <PaginationLink previous onClick={() => handlePageChange(pagination.page - 1)} />
      </PaginationItem>
    );

    // First page
    if (startPage > 1) {
      items.push(
        <PaginationItem key={1}>
          <PaginationLink onClick={() => handlePageChange(1)}>1</PaginationLink>
        </PaginationItem>
      );
      if (startPage > 2) {
        items.push(
          <PaginationItem key="ellipsis1" disabled>
            <PaginationLink>...</PaginationLink>
          </PaginationItem>
        );
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <PaginationItem key={i} active={i === pagination.page}>
          <PaginationLink onClick={() => handlePageChange(i)}>{i}</PaginationLink>
        </PaginationItem>
      );
    }

    // Last page
    if (endPage < pagination.pageCount) {
      if (endPage < pagination.pageCount - 1) {
        items.push(
          <PaginationItem key="ellipsis2" disabled>
            <PaginationLink>...</PaginationLink>
          </PaginationItem>
        );
      }
      items.push(
        <PaginationItem key={pagination.pageCount}>
          <PaginationLink onClick={() => handlePageChange(pagination.pageCount)}>{pagination.pageCount}</PaginationLink>
        </PaginationItem>
      );
    }

    // Next button
    items.push(
      <PaginationItem key="next" disabled={pagination.page === pagination.pageCount}>
        <PaginationLink next onClick={() => handlePageChange(pagination.page + 1)} />
      </PaginationItem>
    );

    return items;
  };

  return (
    <React.Fragment>
      <Header.BreadCrumb color="secondary" isDark={false}>
        <Col xs={12}>
          <h2 className="text-white mb-0">{t("title") || "Auth Logs"}</h2>
        </Col>
      </Header.BreadCrumb>

      <Container className="mt--6" fluid>
        {/* Stats Cards */}
        <Row className="mb-4">
          <Col xs={12} md={4}>
            <StatsCard.Number
              color="default"
              isDark={true}
              number={isLoadingStats ? 0 : stats.total}
              suffix=""
              title={t("totalEvents") || "Total Events"}
              icon="fas fa-sign-in-alt"
            />
          </Col>
          <Col xs={12} md={4}>
            <StatsCard.Number
              color="success"
              isDark={true}
              number={isLoadingStats ? 0 : stats.success}
              suffix=""
              title={t("successfulEvents") || "Successful"}
              icon="fas fa-check-circle"
            />
          </Col>
          <Col xs={12} md={4}>
            <StatsCard.Number
              color="danger"
              isDark={true}
              number={isLoadingStats ? 0 : stats.failed}
              suffix=""
              title={t("failedEvents") || "Failed"}
              icon="fas fa-times-circle"
            />
          </Col>
        </Row>

        {error && (
          <Alert color="danger" className="mb-4">
            {error}
            <button type="button" className="close" onClick={() => setError(null)}>
              <span>&times;</span>
            </button>
          </Alert>
        )}

        {/* Filters */}
        <Card className="mb-4">
          <CardHeader>
            <h4 className="mb-0">
              <i className="fas fa-filter mr-2"></i>
              {t("filters") || "Filters"}
            </h4>
          </CardHeader>
          <CardBody>
            <Row>
              <Col md={2}>
                <FormGroup>
                  <Label for="auth-logs-field-1" className="form-control-label">{t("status") || "Status"}</Label>
                  <Input id="auth-logs-field-1" type="select" name="status" value={filters.status} onChange={handleFilterChange}>
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={2}>
                <FormGroup>
                  <Label for="auth-logs-field-2" className="form-control-label">{t("action") || "Action"}</Label>
                  <Input id="auth-logs-field-2" type="select" name="action" value={filters.action} onChange={handleFilterChange}>
                    {ACTION_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={2}>
                <FormGroup>
                  <Label for="auth-logs-field-3" className="form-control-label">{t("origin") || "Origin"}</Label>
                  <Input id="auth-logs-field-3" type="select" name="origin" value={filters.origin} onChange={handleFilterChange}>
                    {ORIGIN_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
              <Col md={2}>
                <FormGroup>
                  <Label for="auth-logs-field-4" className="form-control-label">{t("email") || "Email"}</Label>
                  <Input id="auth-logs-field-4"
                    type="text"
                    name="email"
                    value={filters.email}
                    onChange={handleFilterChange}
                    placeholder={t("searchByEmail")}
                  />
                </FormGroup>
              </Col>
              <Col md={2}>
                <FormGroup>
                  <Label for="auth-logs-field-5" className="form-control-label">{t("startDate") || "Start Date"}</Label>
                  <Input id="auth-logs-field-5" type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} />
                </FormGroup>
              </Col>
              <Col md={2}>
                <FormGroup>
                  <Label for="auth-logs-field-6" className="form-control-label">{t("endDate") || "End Date"}</Label>
                  <Input id="auth-logs-field-6" type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} />
                </FormGroup>
              </Col>
            </Row>
            <div className="text-right">
              <Button color="secondary" size="sm" onClick={handleClearFilters}>
                <i className="fas fa-times mr-1"></i>
                {t("clearFilters") || "Clear Filters"}
              </Button>
            </div>
          </CardBody>
        </Card>

        {/* Logs Table */}
        <Card>
          <CardHeader className="d-flex justify-content-between align-items-center">
            <h3 className="mb-0">{t("logsList") || "Auth Logs"}</h3>
            <div>
              <span className="text-muted mr-3">
                {t("showing") || "Showing"} {logs.length} {t("of") || "of"} {pagination.total}
              </span>
              <Button color="secondary" size="sm" onClick={loadLogs} disabled={isLoadingLogs}>
                <i className="fas fa-sync-alt mr-1"></i>
                {t("refresh") || "Refresh"}
              </Button>
            </div>
          </CardHeader>
          <CardBody>
            {isLoadingLogs ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-3">{t("loading") || "Loading logs..."}</p>
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-inbox fa-3x mb-3"></i>
                <p>{t("noLogs") || "No auth logs found"}</p>
              </div>
            ) : (
              <>
                <Table responsive hover>
                  <thead className="thead-light">
                    <tr>
                      <th>{t("date") || "Date"}</th>
                      <th>{t("email") || "Email"}</th>
                      <th>{t("action") || "Action"}</th>
                      <th>{t("status") || "Status"}</th>
                      <th>{t("origin") || "Origin"}</th>
                      <th>{t("ipAddress") || "IP Address"}</th>
                      <th>{t("failureReason") || "Failure Reason"}</th>
                      <th>{t("userAgent") || "User Agent"}</th>
                      <th className="text-right">{t("actions") || "Actions"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((log) => (
                      <tr key={log.id}>
                        <td className="text-nowrap">{formatDate(log.createdAt)}</td>
                        <td>{log.email}</td>
                        <td>
                          <Badge color={ACTION_COLORS[log.action ?? "login"] || "secondary"}>
                            {log.action || "login"}
                          </Badge>
                        </td>
                        <td>
                          <Badge color={STATUS_COLORS[log.status ?? ""] || "secondary"}>{log.status}</Badge>
                        </td>
                        <td>
                          <Badge color={ORIGIN_COLORS[log.origin ?? "unknown"] || "secondary"}>
                            <i className={`${ORIGIN_ICONS[log.origin ?? "unknown"] || "fas fa-question-circle"} mr-1`}></i>
                            {log.origin || "unknown"}
                          </Badge>
                        </td>
                        <td>{log.ipAddress || "-"}</td>
                        <td>
                          {log.status === "failed" && log.failureReason ? (
                            <small className="text-danger" title={log.failureReason}>
                              {truncateText(log.failureReason, 50)}
                            </small>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td>
                          <small title={log.userAgent || ""}>{truncateText(log.userAgent, 50)}</small>
                        </td>
                        <td className="text-right text-nowrap">
                          <Button
                            color="link"
                            size="sm"
                            onClick={() => openViewModal(log.id)}
                            title={t("view") || "View"}
                          >
                            <i className="fas fa-eye"></i>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                {/* Pagination */}
                {pagination.pageCount > 1 && (
                  <div className="d-flex justify-content-center mt-4">
                    <Pagination>{renderPagination()}</Pagination>
                  </div>
                )}
              </>
            )}
          </CardBody>
        </Card>
      </Container>

      {/* View Modal */}
      <Modal isOpen={viewModalOpen} toggle={closeViewModal} size="lg">
        <ModalHeader toggle={closeViewModal}>{t("eventDetails") || "Event Details"}</ModalHeader>
        <ModalBody>
          {selectedLog && (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>{t("email") || "Email"}:</strong> {selectedLog.email}
                </Col>
                <Col md={6}>
                  <strong>{t("status") || "Status"}:</strong>{" "}
                  <Badge color={STATUS_COLORS[selectedLog.status ?? ""] || "secondary"}>{selectedLog.status}</Badge>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>{t("action") || "Action"}:</strong>{" "}
                  <Badge color={ACTION_COLORS[selectedLog.action ?? "login"] || "secondary"}>
                    {selectedLog.action || "login"}
                  </Badge>
                </Col>
                <Col md={6}>
                  <strong>{t("date") || "Date"}:</strong> {formatDate(selectedLog.createdAt)}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>{t("ipAddress") || "IP Address"}:</strong> {selectedLog.ipAddress || "-"}
                </Col>
                <Col md={6}>
                  <strong>{t("origin") || "Origin"}:</strong>{" "}
                  <Badge color={ORIGIN_COLORS[selectedLog.origin ?? "unknown"] || "secondary"}>
                    <i className={`${ORIGIN_ICONS[selectedLog.origin ?? "unknown"] || "fas fa-question-circle"} mr-1`}></i>
                    {selectedLog.origin || "unknown"}
                  </Badge>
                </Col>
              </Row>
              {selectedLog.user && (
                <Row className="mb-3">
                  <Col>
                    <strong>{t("user") || "User"}:</strong> {selectedLog.user.username} ({selectedLog.user.email})
                  </Col>
                </Row>
              )}
              {selectedLog.failureReason && (
                <Alert color="danger" className="mb-3">
                  <strong>{t("failureReason") || "Failure Reason"}:</strong> {selectedLog.failureReason}
                </Alert>
              )}
              <div className="mb-3">
                <strong>{t("userAgent") || "User Agent"}:</strong>
                <pre className="bg-light p-2 mt-2 rounded small" style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                  {selectedLog.userAgent || "-"}
                </pre>
              </div>
              {selectedLog.metadata && Object.keys(selectedLog.metadata).length > 0 && (
                <div className="mt-3">
                  <strong>{t("metadata") || "Metadata"}:</strong>
                  <pre className="bg-light p-2 mt-2 rounded small">
                    {JSON.stringify(selectedLog.metadata, null, 2)}
                  </pre>
                </div>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeViewModal}>
            {t("close") || "Close"}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default AuthLogs;
